import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './Dashboard.css';

/**
 * Admin Dashboard
 * Shows key metrics and recent activity for trade license management
 */

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/trade-license/reports/application_summary/');
      setStats(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dashboard statistics');
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Welcome to Trade License Management Portal</p>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {stats && (
        <>
          {/* Key Metrics */}
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon">📋</div>
              <div className="metric-content">
                <h3>Total Applications</h3>
                <p className="metric-value">{stats.total || 0}</p>
                <small>All time</small>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">⏳</div>
              <div className="metric-content">
                <h3>In Progress</h3>
                <p className="metric-value">{stats.submitted || 0}</p>
                <small>Pending action</small>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">💰</div>
              <div className="metric-content">
                <h3>Pending Payment</h3>
                <p className="metric-value">{stats.pending_payment || 0}</p>
                <small>Requires collection</small>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">✅</div>
              <div className="metric-content">
                <h3>Approved</h3>
                <p className="metric-value">{stats.approved || 0}</p>
                <small>Ready for issuance</small>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">🎫</div>
              <div className="metric-content">
                <h3>Licenses Issued</h3>
                <p className="metric-value">{stats.issued || 0}</p>
                <small>Active licenses</small>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">❌</div>
              <div className="metric-content">
                <h3>Rejected</h3>
                <p className="metric-value">{stats.rejected || 0}</p>
                <small>Rejected applications</small>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="dashboard-row">
            <div className="card">
              <h2>Quick Actions</h2>
              <div className="actions-grid">
                <a href="/office/applications?status=submitted" className="action-btn">
                  <span>📥</span>
                  <span>View New Applications</span>
                </a>
                <a href="/office/inspections?status=scheduled" className="action-btn">
                  <span>🔍</span>
                  <span>Schedule Inspections</span>
                </a>
                <a href="/office/applications?status=in_progress" className="action-btn">
                  <span>⏱️</span>
                  <span>Pending Approvals</span>
                </a>
                <a href="/office/payments" className="action-btn">
                  <span>💳</span>
                  <span>Verify Payments</span>
                </a>
              </div>
            </div>
          </div>

          {/* System Information */}
          <div className="dashboard-row">
            <div className="card info-card">
              <h2>System Information</h2>
              <dl className="info-list">
                <dt>Portal Version:</dt>
                <dd>1.0.0</dd>
                
                <dt>Last Updated:</dt>
                <dd>Today</dd>
                
                <dt>System Status:</dt>
                <dd className="status-ok">✓ All Systems Operational</dd>
              </dl>
            </div>

            <div className="card">
              <h2>Recent Activity</h2>
              <p style={{textAlign: 'center', color: '#999', padding: '20px'}}>
                No recent activity
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
